/**
 * Frontend wrapper around the v48/v49 widget bundle routes.
 *
 * All requests go through `api.authFetch` (Firebase ID token) except the
 * public catalog which is anonymous-friendly. Routes:
 *
 *   GET  /api/widgets/public                — public catalog
 *   GET  /api/widgets                       — my widgets
 *   POST /api/widgets                       — create empty widget
 *   GET  /api/widgets/{id}                  — fetch one (legacy summary)
 *   GET  /api/widgets/{id}/bundle?tag=...   — bundle.zip
 *   GET  /api/widgets/{id}/files/{p}?tag=…  — single file (for preview/edit)
 *   POST /api/widgets/{id}/bundle/commit    — multipart zip → new version
 *   POST /api/widgets/{id}/publish          — body: { to: release }
 *   POST /api/widgets/{id}/scratch/init     — seed Redis scratch with dev
 *   POST /api/widgets/{id}/scratch/commit   — snapshot scratch → new version
 *   GET  /api/widgets/{id}/data-schema      — per-widget data schema (?tag=)
 */
import { api } from './api';

// Cold-start dedupe: during the auth-sync window (StrictMode mounts the
// catalog twice, listMine/listPublic each retry on 401, and the api-server
// pool warms up on first call) Growth saw 4-6 identical 500/401 errors
// surface to the console per route in a few seconds. Suppress duplicates:
// the same {label, status} key only logs once per 5s window. Throws are
// unchanged — callers' catch blocks still run, we only quiet the console.
const _ERR_WINDOW_MS = 5_000;
const _lastErrAt = new Map();
function _shouldLog(label, status) {
  const key = `${label}:${status}`;
  const now = Date.now();
  const prev = _lastErrAt.get(key) || 0;
  if (now - prev < _ERR_WINDOW_MS) return false;
  _lastErrAt.set(key, now);
  return true;
}

async function jsonOrThrow(resp, label) {
  if (!resp.ok) {
    const text = await resp.text().catch(() => '');
    const summary = `${label} ${resp.status}: ${text.slice(0, 200)}`;
    if (_shouldLog(label, resp.status)) {
      // One bounded warning per route per window — easier to spot
      // a real regression in the console without scrolling past a wall
      // of identical retries.
      console.warn(`[widgetApi] ${summary}`);
    }
    // Attach structured fields so callers branch on the status (e.g. 404 =
    // "instance was deleted") instead of parsing the message string. Single
    // source of truth for HTTP error metadata across every widgetApi consumer.
    const err = new Error(summary);
    err.status = resp.status;
    err.label = label;
    err.body = text;
    throw err;
  }
  return resp.json();
}

// Encode a bundle-relative path, preserving `/` separators but escaping
// segment-unsafe chars. Used by scratch file routes that take a path
// suffix rather than a query param.
function _encodePath(path) {
  return String(path).split('/').map(encodeURIComponent).join('/');
}

export const widgetApi = {
  async listPublic(limit = 50, offset = 0, { widget_type, scope = 'all' } = {}) {
    // The studio/playground catalog is an authoring surface → show the FULL
    // public set (scope=all), not the curated app Gallery "Discover" subset.
    const qs = new URLSearchParams({ limit: String(limit), offset: String(offset), scope });
    if (widget_type) qs.set('widget_type', widget_type);
    const resp = await fetch(`/api/widgets/public?${qs}`);
    return jsonOrThrow(resp, 'listPublic');
  },

  async listMine({ widget_type } = {}) {
    // The studio is the admin authoring surface: include_shared=true asks the
    // server for the in-house shared set (every OTHER user's PRIVATE widgets) so
    // an admin sees all private standard widgets + templates. The server gates
    // it on is_admin, so non-admins get only their own. The app never sends it.
    const params = new URLSearchParams({ include_shared: 'true' });
    if (widget_type) params.set('widget_type', widget_type);
    const resp = await api.authFetch(`/api/widgets?${params.toString()}`);
    return jsonOrThrow(resp, 'listMine');
  },

  async create({ slug, title, description, widget_type } = {}) {
    const resp = await api.authFetch('/api/widgets', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        // The widget's id, human-chosen ("personal-studio"): same logical
        // widget ⇒ same id in every env; duplicates 409.
        slug,
        title: title || null,
        description: description || null,
        widget_type: widget_type === 'template' ? 'template' : 'standard',
      }),
    });
    return jsonOrThrow(resp, 'create');
  },

  async getWidget(widgetId) {
    const resp = await api.authFetch(`/api/widgets/${encodeURIComponent(widgetId)}`);
    return jsonOrThrow(resp, 'getWidget');
  },

  async initScratch(widgetId, sessionId) {
    const qs = new URLSearchParams({ session_id: sessionId });
    const resp = await api.authFetch(
      `/api/widgets/${encodeURIComponent(widgetId)}/scratch/init?${qs}`,
      { method: 'POST' },
    );
    return jsonOrThrow(resp, 'initScratch');
  },

  /**
   * Server-side check: does the per-session scratch differ from the
   * widget's current dev tag? Returns `{dirty: bool}`. The editor calls
   * this once on mount to derive both Scratch-tab visibility and the
   * initial preview tab — the server is the single source of truth for
   * "is there something to commit?".
   */
  async scratchStatus(widgetId, sessionId) {
    const qs = new URLSearchParams({ session_id: sessionId });
    const resp = await api.authFetch(
      `/api/widgets/${encodeURIComponent(widgetId)}/scratch/status?${qs}`,
    );
    return jsonOrThrow(resp, 'scratchStatus');
  },

  async commitScratch(widgetId, sessionId, note = null) {
    const qs = new URLSearchParams({ session_id: sessionId });
    if (note) qs.set('note', note);
    const resp = await api.authFetch(
      `/api/widgets/${encodeURIComponent(widgetId)}/scratch/commit?${qs}`,
      { method: 'POST' },
    );
    return jsonOrThrow(resp, 'commitScratch');
  },

  // SSE-streaming variant of commitScratch. Calls onProgress({step,pct}) at
  // each backend step and resolves with the WidgetBundleVersionSummary on
  // the final `complete` event. Throws on error events or non-2xx responses.
  async commitScratchStream(widgetId, sessionId, note = null, onProgress = null, signal = null) {
    const qs = new URLSearchParams({ session_id: sessionId });
    if (note) qs.set('note', note);
    const resp = await api.authFetch(
      `/api/widgets/${encodeURIComponent(widgetId)}/scratch/commit?${qs}`,
      { method: 'POST', signal },
    );
    if (!resp.ok) {
      // Non-2xx before stream starts — surface as a regular error.
      await jsonOrThrow(resp, 'commitScratchStream');
    }
    const reader = resp.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      // Split on blank lines (SSE frame boundaries)
      const parts = buffer.split('\n\n');
      buffer = parts.pop() ?? '';
      for (const frame of parts) {
        let eventType = 'message';
        let dataStr = '';
        for (const line of frame.split('\n')) {
          if (line.startsWith('event: ')) eventType = line.slice(7).trim();
          else if (line.startsWith('data: ')) dataStr += line.slice(6);
        }
        if (!dataStr) continue;
        const data = JSON.parse(dataStr);
        if (eventType === 'progress') {
          onProgress?.(data);
        } else if (eventType === 'complete') {
          return data;
        } else if (eventType === 'error') {
          throw new Error(data.message || 'commit failed');
        }
      }
    }
    throw new Error('Commit stream ended without a completion event');
  },

  /**
   * Pin an EXACT committed version to the PUBLIC pin (the studio "Publish to
   * Public"). Body: { to: 'public', version_label, note }. Schema-gated — the
   * server returns 422 with `detail.issues` when the version fails validation.
   */
  async publish(widgetId, versionLabel, note = null) {
    const resp = await api.authFetch(
      `/api/widgets/${encodeURIComponent(widgetId)}/publish`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ to: 'public', version_label: versionLabel, note }),
      },
    );
    return jsonOrThrow(resp, 'publish');
  },

  /**
   * Apply a cover/landing-type change DIRECTLY to a tagged version's widget.json
   * (the studio ✓/✗ apply-to-version). No scratch, no new version — patches the
   * dev/release bundle in place. `updates` = { cover_type?, cover_preset?,
   * landing_type?, note? }. Returns the refreshed bundle summary (fresh tags).
   */
  async patchVersionManifest(widgetId, tag, updates = {}) {
    const resp = await api.authFetch(
      `/api/widgets/${encodeURIComponent(widgetId)}/bundle/${encodeURIComponent(tag)}/manifest`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates),
      },
    );
    return jsonOrThrow(resp, 'patchVersionManifest');
  },

  /**
   * Clear the release tag pointer (also flips is_public=false).
   * Returns the refreshed bundle summary (so the caller can re-render tags).
   */
  async clearTag(widgetId, tag, note = null) {
    const resp = await api.authFetch(
      `/api/widgets/${encodeURIComponent(widgetId)}/tags/${encodeURIComponent(tag)}/clear`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(note ? { note } : {}),
      },
    );
    return jsonOrThrow(resp, 'clearTag');
  },

  /**
   * Move the dev pointer to whatever version is currently tagged release.
   * Used by "Overwrite to Dev" — base further edits on the shipped version.
   */
  async overwriteDev(widgetId, source, note = null) {
    const qs = new URLSearchParams({ source });
    const resp = await api.authFetch(
      `/api/widgets/${encodeURIComponent(widgetId)}/tags/dev/overwrite?${qs}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(note ? { note } : {}),
      },
    );
    return jsonOrThrow(resp, 'overwriteDev');
  },

  /**
   * Wipe the per-session scratch and re-seed from the current dev tag.
   * "Delete Scratch" in the editor — discards in-flight edits.
   */
  async deleteScratch(widgetId, sessionId) {
    const qs = new URLSearchParams({ session_id: sessionId });
    const resp = await api.authFetch(
      `/api/widgets/${encodeURIComponent(widgetId)}/scratch/delete?${qs}`,
      { method: 'POST' },
    );
    return jsonOrThrow(resp, 'deleteScratch');
  },

  /**
   * Owner-only delete. Returns true on 204; throws on non-2xx (incl 404 —
   * already gone). Callers should remove the tile from local state on
   * success without re-fetching the whole catalog.
   */
  async deleteWidget(widgetId) {
    const resp = await api.authFetch(
      `/api/widgets/${encodeURIComponent(widgetId)}`,
      { method: 'DELETE' },
    );
    if (resp.status === 204) return true;
    if (!resp.ok) {
      const text = await resp.text().catch(() => '');
      throw new Error(`deleteWidget ${resp.status}: ${text.slice(0, 200)}`);
    }
    return true;
  },

  /**
   * Flip is_public=False without moving the release/dev tag pointers.
   * The widget leaves /api/widgets/public but `release` tag still exists
   * so the owner can flip back via /publish {to:'release'} later.
   */
  async unpublish(widgetId, note = null) {
    const resp = await api.authFetch(
      `/api/widgets/${encodeURIComponent(widgetId)}/unpublish`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(note ? { note } : {}),
      },
    );
    return jsonOrThrow(resp, 'unpublish');
  },

  /**
   * Build a preview URL for a single file in a tagged bundle. The iframe
   * src points here so updates can cache-bust via a sha-suffix.
   */
  fileUrl(widgetId, path, { tag = 'dev', sha } = {}) {
    const qs = new URLSearchParams({ tag });
    if (sha) qs.set('v', sha);
    return `/api/widgets/${encodeURIComponent(widgetId)}/files/${path}?${qs}`;
  },

  bundleUrl(widgetId, tag = 'dev') {
    const qs = new URLSearchParams({ tag });
    return `/api/widgets/${encodeURIComponent(widgetId)}/bundle?${qs}`;
  },

  /**
   * Fetch the bundle.zip for ``tag`` with auth and trigger a browser download.
   * Honors the server's ``Content-Disposition`` filename when present so the
   * downloaded file matches the canonical ``widget-{id}-{version}.zip`` name.
   * Uses ``api.authFetch`` so private widgets (non-release) work — the iframe
   * preview path's blob: trick isn't applicable when the user wants the file
   * on disk.
   */
  async downloadBundle(widgetId, { tag = 'dev', filename } = {}) {
    const url = this.bundleUrl(widgetId, tag);
    const resp = await api.authFetch(url);
    if (!resp.ok) {
      const text = await resp.text().catch(() => '');
      throw new Error(`downloadBundle ${resp.status}: ${text.slice(0, 200)}`);
    }
    const blob = await resp.blob();
    let suggested = filename;
    if (!suggested) {
      const cd = resp.headers.get('content-disposition') || '';
      const m = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(cd);
      suggested = m?.[1] || `widget-${widgetId}-${tag}.zip`;
    }
    const obj = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = obj;
    a.download = suggested;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(obj), 1000);
    return { filename: suggested, size: blob.size };
  },

  /**
   * Upload a bundle.zip to overwrite the per-session scratch. Mirror of the
   * Download ZIP roundtrip. Backend unpacks the zip, removes any files that
   * existed in scratch but not in the upload, writes everything else, and
   * fires widget_file_updated SSE events per path so the editor preview
   * iframe refreshes.
   */
  async uploadScratchZip(widgetId, sessionId, file) {
    const qs = new URLSearchParams({ session_id: sessionId });
    const fd = new FormData();
    fd.append('file', file);
    const resp = await api.authFetch(
      `/api/widgets/${encodeURIComponent(widgetId)}/scratch/upload?${qs}`,
      { method: 'POST', body: fd },
    );
    return jsonOrThrow(resp, 'uploadScratchZip');
  },

  /**
   * Build a preview URL for a single file out of the per-session Redis
   * scratch. The editor uses this BEFORE the first commit so the iframe
   * reflects autosaved keystrokes — there's no `dev` tag yet to read from.
   * Iframe `src` carries no Authorization header; auth is via Firebase
   * session cookie set during /api/auth/login.
   */
  scratchFileUrl(widgetId, path, { sessionId, sha } = {}) {
    const qs = new URLSearchParams({ session_id: sessionId });
    if (sha) qs.set('v', sha);
    return `/api/widgets/${encodeURIComponent(widgetId)}/scratch/files/${_encodePath(path)}?${qs}`;
  },

  /**
   * Read a single scratch file as text (auth via authFetch). Used by the
   * editor to re-hydrate the textarea after remount, before any commit has
   * pinned a dev tag.
   */
  async readScratchFile(widgetId, path, sessionId) {
    const qs = new URLSearchParams({ session_id: sessionId });
    const resp = await api.authFetch(
      `/api/widgets/${encodeURIComponent(widgetId)}/scratch/files/${_encodePath(path)}?${qs}`,
    );
    if (!resp.ok) throw new Error(`readScratchFile ${path} ${resp.status}`);
    return resp.text();
  },

  /**
   * Persist a manual editor edit into the per-session Redis scratch via
   * PUT /api/widgets/{id}/scratch/files/{path}?session_id=…
   *
   * Body is raw bytes — we pass a string and let fetch encode it as UTF-8.
   * Returns { ok, sha256, size }. The API also fires a widget_file_updated
   * SSE event, but the editor doesn't need to wait for it: the same
   * textarea that triggered the save is already showing the new content.
   */
  async saveFile(widgetId, path, content, sessionId) {
    const qs = new URLSearchParams({ session_id: sessionId });
    const resp = await api.authFetch(
      `/api/widgets/${encodeURIComponent(widgetId)}/scratch/files/${_encodePath(path)}?${qs}`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/octet-stream' },
        body: content ?? '',
      },
    );
    return jsonOrThrow(resp, 'saveFile');
  },

  /** Create a new (empty) scratch file. PUT is create-or-overwrite (#2320 D6). */
  async createScratchFile(widgetId, path, sessionId, content = '') {
    return this.saveFile(widgetId, path, content, sessionId);
  },

  /** Delete one scratch file (#2320 D6). Refuses widget.json server-side. */
  async deleteScratchFile(widgetId, path, sessionId) {
    const qs = new URLSearchParams({ session_id: sessionId });
    const resp = await api.authFetch(
      `/api/widgets/${encodeURIComponent(widgetId)}/scratch/files/${_encodePath(path)}?${qs}`,
      { method: 'DELETE' },
    );
    return jsonOrThrow(resp, 'deleteScratchFile');
  },

  /** Rename/move one scratch file (#2320 D6). */
  async renameScratchFile(widgetId, fromPath, toPath, sessionId) {
    const qs = new URLSearchParams({ session_id: sessionId });
    const resp = await api.authFetch(
      `/api/widgets/${encodeURIComponent(widgetId)}/scratch/rename?${qs}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ from_path: fromPath, to_path: toPath }),
      },
    );
    return jsonOrThrow(resp, 'renameScratchFile');
  },

  /**
   * The studio VERSIONS list (v96): every committed version label, local
   * rows merged with the cross-env shared list. Each entry carries THIS
   * env's badges: { version_label, private, public, local, shared,
   * origin_env, note, name, app_min_version, file_count, created_at }.
   * `app_min_version` (v133) = min app semver to render that version (or null).
   * Response also has { shared_enabled, shared_key, env }.
   */
  async listVersionLabels(widgetId) {
    const resp = await api.authFetch(
      `/api/widgets/${encodeURIComponent(widgetId)}/version-labels`,
    );
    return jsonOrThrow(resp, 'listVersionLabels');
  },

  /**
   * Load one VERSIONS-list version into the per-session scratch (v96) —
   * the studio's double-click on an un-badged version. A shared-only
   * version is adopted into this env first (tags untouched). The scratch
   * records the origin label so an untouched re-commit moves the dev tag
   * to this version instead of minting a duplicate label.
   */
  async checkoutVersion(widgetId, versionLabel, sessionId) {
    const qs = new URLSearchParams({ session_id: sessionId });
    const resp = await api.authFetch(
      `/api/widgets/${encodeURIComponent(widgetId)}/versions/${encodeURIComponent(versionLabel)}/checkout?${qs}`,
      { method: 'POST' },
    );
    return jsonOrThrow(resp, 'checkoutVersion');
  },

  /** The single WIDGET_SCHEMA_VERSION macro — the grid badges anything below it. */
  async schemaVersion() {
    const resp = await api.authFetch('/api/widgets/schema/version');
    return jsonOrThrow(resp, 'schemaVersion');
  },

  /**
   * Run the schema checker against one owned widget's tagged bundle (#2320 D2).
   * Returns { passed, version, recorded_version, outdated, issues[], upgraded }.
   * Green auto-stamps the bundle (clears the ⚠️).
   */
  async checkWidgetSchema(widgetId, { version = null, tag = 'dev' } = {}) {
    const qs = new URLSearchParams(version ? { version } : { tag });
    const resp = await api.authFetch(
      `/api/widgets/${encodeURIComponent(widgetId)}/schema/check?${qs}`,
      { method: 'POST' },
    );
    return jsonOrThrow(resp, 'checkWidgetSchema');
  },

  /**
   * Per-widget data schema (v3 contract). Returns { widget_id, widget_type,
   * tag, data_schema, data_schema_version, data_inputs, required_core,
   * contract_version, recorded_contract_version, issues }. `data_schema` is
   * null for pre-v3 bundles that don't declare one in widget.json.
   */
  async getWidgetDataSchema(widgetId, { version = null, tag = 'dev' } = {}) {
    const qs = new URLSearchParams(version ? { version } : { tag });
    const resp = await api.authFetch(
      `/api/widgets/${encodeURIComponent(widgetId)}/data-schema?${qs}`,
    );
    return jsonOrThrow(resp, 'getWidgetDataSchema');
  },

  /**
   * Read a single file as text, with auth. Used by the editor to populate
   * the code pane and refresh after agent writes.
   */
  async readFile(widgetId, path, { version = null, tag = 'dev' } = {}) {
    const qs = new URLSearchParams(version ? { version } : { tag });
    const resp = await api.authFetch(
      `/api/widgets/${encodeURIComponent(widgetId)}/files/${path}?${qs}`,
    );
    if (!resp.ok) throw new Error(`readFile ${path} ${resp.status}`);
    return resp.text();
  },

  /**
   * Fetch a tagged file as a Blob preserving the server's Content-Type so
   * the caller can build an object URL that the iframe consumes without
   * an Authorization header. The plain bytes are the same as readFile()
   * but the Blob carries the MIME type the served file actually wants
   * (text/html with charset utf-8, image/png, etc.).
   *
   * The iframe `src` MUST be a blob: URL because iframes can only carry
   * cookies — they cannot forward the Firebase ID token header that the
   * editor's normal authFetch path uses. Setting iframe.src directly to
   * /api/widgets/.../files/... would always 401. Throws on non-OK.
   */
  async readFileAsBlob(widgetId, path, { version = null, tag = 'dev' } = {}) {
    // Prefer an exact version pin (studio version viewer) — the server serves
    // that committed version's file. Falls back to a tag walk for back-compat
    // callers (WidgetCatalog cover/index preview) that still pass { tag }.
    const qs = new URLSearchParams(version ? { version } : { tag });
    const resp = await api.authFetch(
      `/api/widgets/${encodeURIComponent(widgetId)}/files/${path}?${qs}`,
    );
    if (!resp.ok) throw new Error(`readFileAsBlob ${path} ${resp.status}`);
    const ct = resp.headers.get('content-type') || 'application/octet-stream';
    const buf = await resp.arrayBuffer();
    return new Blob([buf], { type: ct });
  },

  /**
   * Same as readFileAsBlob but reads from the per-session Redis scratch
   * (pre-commit live state). The iframe preview uses this in the editor
   * before the first commit produces a `dev` tag. Falls back is handled
   * by the caller — we just throw on 404 so they can try the dev tag.
   */
  /**
   * Mark a widget as used by the signed-in user — upserts a thin
   * `user_widgets` row + bumps `last_used_at`. Replaces the older install
   * flow (no per-user "instance" id anymore). Returns
   * `{ widget_id, first_used_at, last_used_at }`. Idempotent.
   */
  async markUsed(widgetId, settings = null) {
    const body = settings ? { settings } : {};
    const resp = await api.authFetch(
      `/api/widgets/${encodeURIComponent(widgetId)}/use`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      },
    );
    return jsonOrThrow(resp, 'markUsed');
  },

  async readScratchFileAsBlob(widgetId, path, sessionId) {
    const qs = new URLSearchParams({ session_id: sessionId });
    const resp = await api.authFetch(
      `/api/widgets/${encodeURIComponent(widgetId)}/scratch/files/${_encodePath(path)}?${qs}`,
    );
    if (!resp.ok) throw new Error(`readScratchFileAsBlob ${path} ${resp.status}`);
    const ct = resp.headers.get('content-type') || 'application/octet-stream';
    const buf = await resp.arrayBuffer();
    return new Blob([buf], { type: ct });
  },
};

/**
 * widgetInstances — per-user widget instances (separate from the Studio
 * authoring catalog). Each instance is a row in `widget_instances`:
 *
 *   • Standard widgets — exactly one instance per (user, widget) pair.
 *   • Templates — many instances allowed, each with its own `title`.
 *
 * Endpoints mounted under /api/widget-instances and
 * /api/widgets/{widget_id}/instantiate on the api-server.
 */
export const widgetInstances = {
  /**
   * List the current user's instances, optionally filtered by widget_type.
   * With `log_card_id`, returns only the instance that owns a data row with
   * that id (≤1) — used by the widget_suggestion card to restore its
   * "Created" state after a reload.
   */
  async list({ widget_type, log_card_id, signal } = {}) {
    const qs = new URLSearchParams();
    if (widget_type) qs.set('widget_type', widget_type);
    if (log_card_id) qs.set('log_card_id', log_card_id);
    const s = qs.toString();
    const resp = await api.authFetch(`/api/widget-instances${s ? `?${s}` : ''}`, signal ? { signal } : undefined);
    return jsonOrThrow(resp, 'widgetInstances.list');
  },

  /**
   * Create an instance of `widgetId`. Idempotent for standard widgets
   * (returns existing row). Always-new for templates — pass a `title`.
   */
  async instantiate(widgetId, { title, settings } = {}) {
    const body = {};
    if (title) body.title = title;
    if (settings) body.settings = settings;
    const resp = await api.authFetch(
      `/api/widgets/${encodeURIComponent(widgetId)}/instantiate`,
      { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) },
    );
    return jsonOrThrow(resp, 'widgetInstances.instantiate');
  },

  /** Detail for a single instance. */
  async get(instanceId) {
    const resp = await api.authFetch(`/api/widget-instances/${encodeURIComponent(instanceId)}`);
    return jsonOrThrow(resp, 'widgetInstances.get');
  },

  /**
   * Resolve which of the user's sessions contains a given widget_log card
   * (Model B — replaces the per-entry source_session_id backlink). Used by the
   * session-badge jump to land on the exact card. Returns the session id or null.
   */
  async sessionByLogCard(logCardId) {
    if (!logCardId) return null;
    const resp = await api.authFetch(`/api/users/sessions/by-log-card/${encodeURIComponent(logCardId)}`);
    const j = await jsonOrThrow(resp, 'widgetInstances.sessionByLogCard');
    return j?.session_id || null;
  },

  /**
   * Shallow-merge per-instance settings (null deletes a key). Per-instance
   * cover_type / cover_preset / landing_type overrides win over the widget
   * manifest on every tile (app catalog + web).
   */
  async patchSettings(instanceId, settings) {
    const resp = await api.authFetch(
      `/api/widget-instances/${encodeURIComponent(instanceId)}/settings`,
      { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ settings }) },
    );
    return jsonOrThrow(resp, 'widgetInstances.patchSettings');
  },

  /** Drop the instance row; the data feed cascades via FK. */
  async remove(instanceId) {
    const resp = await api.authFetch(
      `/api/widget-instances/${encodeURIComponent(instanceId)}`,
      { method: 'DELETE' },
    );
    return jsonOrThrow(resp, 'widgetInstances.remove');
  },

  /**
   * Paginated capture feed for the instance. With `log_card_id`, returns
   * at most one row (used by LogCard self-refresh).
   */
  async data(instanceId, { limit = 50, before = null, log_card_id = null, format = 'proxy', signal } = {}) {
    const qs = new URLSearchParams({ limit: String(limit) });
    if (before) qs.set('before', before);
    if (log_card_id) qs.set('log_card_id', log_card_id);
    // `signed` resolves media:// to 1h GCS signed URLs. Required when the
    // result is injected into the SANDBOXED widget iframe: its <img>/bg-image
    // subresource requests run from an opaque origin (sandbox="allow-scripts",
    // no allow-same-origin) so the SameSite=strict session cookie is NOT sent
    // and the /api/media proxy 401s → blank album tiles. A pre-signed URL
    // needs no auth. Storage stays media://; this is read-time only.
    if (format && format !== 'proxy') qs.set('format', format);
    const resp = await api.authFetch(
      `/api/widget-instances/${encodeURIComponent(instanceId)}/data?${qs}`,
      signal ? { signal } : undefined,
    );
    return jsonOrThrow(resp, 'widgetInstances.data');
  },

  /**
   * Append a row. Idempotent in the "logged" direction when `log_card_id`
   * is set (the server clears `canceled_at` on repeat).
   */
  async appendData(instanceId, { kind, payload, log_card_id = null, source_session_id = null }) {
    const resp = await api.authFetch(
      `/api/widget-instances/${encodeURIComponent(instanceId)}/data`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ kind, payload, log_card_id, source_session_id }),
      },
    );
    return jsonOrThrow(resp, 'widgetInstances.appendData');
  },

  /** Cancel by `log_card_id` — sets `canceled_at = now()`, idempotent. */
  async cancelData(instanceId, logCardId) {
    const qs = new URLSearchParams({ log_card_id: logCardId });
    const resp = await api.authFetch(
      `/api/widget-instances/${encodeURIComponent(instanceId)}/data?${qs}`,
      { method: 'DELETE' },
    );
    return jsonOrThrow(resp, 'widgetInstances.cancelData');
  },

  /**
   * Attach more images to one data entry (the data view's gallery "+"). Appends
   * `addImages` (media:// refs or /api/media proxy URLs) to `payload.images[]`
   * server-side. Returns `{ entry }` with the updated payload.
   */
  async attachImages(instanceId, entryId, addImages) {
    const resp = await api.authFetch(
      `/api/widget-instances/${encodeURIComponent(instanceId)}/data/${encodeURIComponent(entryId)}`,
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ add_images: Array.isArray(addImages) ? addImages : [addImages] }),
      },
    );
    return jsonOrThrow(resp, 'widgetInstances.attachImages');
  },

  /**
   * REPLACE one entry's image list (deterministic per-photo delete — the
   * direct `item_action` path). Mirrors the app's `setInstanceImages`: clear,
   * then re-add via `add_images` so the server normalizes any signed URL back
   * to its canonical media:// handle (an expiring URL is never stored).
   */
  async setImages(instanceId, entryId, images) {
    const resp = await api.authFetch(
      `/api/widget-instances/${encodeURIComponent(instanceId)}/data/${encodeURIComponent(entryId)}`,
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ payload: { images: [], image_url: null }, add_images: images }),
      },
    );
    return jsonOrThrow(resp, 'widgetInstances.setImages');
  },

  /**
   * Permanently delete ONE logged item from the collection by its entry id —
   * the host-owned item CRUD delete (long-press → options → Delete). Sets
   * `deleted_at` server-side so the row drops out of the render feed. Distinct
   * from `cancelData` (the Log⇄Cancel toggle keyed by log_card_id). Idempotent.
   */
  async deleteData(instanceId, entryId) {
    const resp = await api.authFetch(
      `/api/widget-instances/${encodeURIComponent(instanceId)}/data/${encodeURIComponent(entryId)}`,
      { method: 'DELETE' },
    );
    return jsonOrThrow(resp, 'widgetInstances.deleteData');
  },

  /** Sessions linked to this instance. */
  async sessions(instanceId, { limit = 50 } = {}) {
    const qs = new URLSearchParams({ limit: String(limit) });
    const resp = await api.authFetch(
      `/api/widget-instances/${encodeURIComponent(instanceId)}/sessions?${qs}`,
    );
    return jsonOrThrow(resp, 'widgetInstances.sessions');
  },

  /**
   * Find-or-create the instance's single persistent `widget_main` session — the
   * config/edit/ask session opened by the widget view's chat button. Returns
   * `{ session_id, created }`. Idempotent: repeated taps reuse the same session.
   */
  async mainSession(instanceId) {
    const resp = await api.authFetch(
      `/api/widget-instances/${encodeURIComponent(instanceId)}/main-session`,
      { method: 'POST' },
    );
    return jsonOrThrow(resp, 'widgetInstances.mainSession');
  },
};

/**
 * Atomic instantiate-and-log endpoint used by the chat-agent suggestion
 * click path (S15). Standard widgets: idempotent. Templates: new instance
 * each call. Returns { instance, data_row }.
 */
export async function instantiateAndLog({ widget_id, log_card_id, kind, payload, title, source_session_id }) {
  const resp = await api.authFetch(
    '/api/widget-instances',
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ widget_id, log_card_id, kind, payload, title, source_session_id }),
    },
  );
  return jsonOrThrow(resp, 'instantiateAndLog');
}

/** @deprecated Use `widgetInstances` instead. Kept as a thin shim for legacy callers. */
export const userWidgets = widgetInstances;
